import React from 'react';
import { motion } from 'framer-motion';
import PageTemplate from './PageTemplate';

import '../../../../assests/css/customer/Footer/PrivacyPolicy.css';
import MyNavbar from '../../Mynavbar/MyNavbar';
import Footer from '../Footer';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const EFFECTIVE_DATE = 'November 1, 2025';
const CONTACT_EMAIL = 'wecare@moonlitonline.pk';

const Privacy = () => {
  return (
    <>
      <MyNavbar />
      
      <div className="privacy-container">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="privacy-title"
        >
          Privacy Policy
        </motion.h1>

        <motion.div
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.6, ease: 'easeOut' }}
          variants={{
            visible: { transition: { staggerChildren: 0.12 } }
          }}
        >
          <motion.p variants={fadeUp} className="privacy-date">
            <strong>Effective Date:</strong> {EFFECTIVE_DATE}
          </motion.p>

          <PrivacyBlock title="Overview">
            Moonlit is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy outlines how we collect, use, store, and protect your data when you interact with our website and services. By using moonlit.pk, you acknowledge and agree to the practices described in this policy.
            <div className="security-badge">Secure & Encrypted</div>
          </PrivacyBlock>

          <PrivacyBlock title="Information We Collect">
            We collect personal information necessary to provide you with our services, including:
            <ul>
              <li>Name, email address, and phone number during account registration</li>
              <li>Billing and shipping addresses for order fulfillment</li>
              <li>Payment information processed securely through encrypted gateways</li>
              <li>Browsing behavior and preferences to enhance your shopping experience</li>
              <li>Device and browser information for security and optimization purposes</li>
            </ul>
          </PrivacyBlock>

          <PrivacyBlock title="How We Use Your Information">
            Your personal data is used exclusively for the following purposes:
            <ul>
              <li>Processing and fulfilling your orders efficiently</li>
              <li>Communicating with you about order status and updates</li>
              <li>Providing customer support and resolving inquiries</li>
              <li>Sending marketing communications and promotional offers (with your consent)</li>
              <li>Improving our website functionality and user experience</li>
              <li>Preventing fraud and ensuring platform security</li>
            </ul>
          </PrivacyBlock>

          <PrivacyBlock title="Data Security">
            We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. All payment transactions are encrypted using SSL technology, and sensitive data is stored securely on protected servers.
            <div className="privacy-notice">
              <strong>Important:</strong>
              We never store your complete credit card information on our servers. All payment data is handled by certified payment gateways.
            </div>
          </PrivacyBlock>

          <PrivacyBlock title="Marketing Communications">
            With your consent, we may send you promotional emails about new products, special offers, and updates. You can opt out of marketing communications at any time by clicking the unsubscribe link in our emails or contacting us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} aria-label="Contact Moonlit">
              {CONTACT_EMAIL}
            </a>.
          </PrivacyBlock>

          <PrivacyBlock title="Your Privacy Rights">
            <div className="privacy-rights">
              <h4>You have the right to:</h4>
              <ul>
                <li>Access and review the personal data we hold about you</li>
                <li>Request corrections to inaccurate or incomplete information</li>
                <li>Request deletion of your personal data (subject to legal obligations)</li>
                <li>Withdraw consent for data processing at any time</li>
                <li>Object to certain types of data processing</li>
                <li>Receive a copy of your data in a portable format</li>
              </ul>
            </div>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} aria-label="Contact Moonlit">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </PrivacyBlock>

          <PrivacyBlock title="Third-Party Disclosure">
            We do not sell, trade, or transfer your personal information to third parties without your consent, except for trusted service providers who assist us in operating our website and conducting business (e.g., payment processors, shipping partners). These parties are contractually obligated to keep your information confidential and secure.
          </PrivacyBlock>

          <PrivacyBlock title="Cookies & Tracking">
            We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user preferences. You can manage cookie settings through your browser, but disabling cookies may affect certain features of our website.
          </PrivacyBlock>

          <PrivacyBlock title="Children's Privacy">
            Moonlit does not knowingly collect or solicit personal information from individuals under the age of 13. If we become aware that we have collected such information, we will promptly delete it from our records. Parents or guardians who believe their child has provided personal information should contact us immediately.
          </PrivacyBlock>

          <PrivacyBlock title="Policy Updates">
            We reserve the right to update this Privacy Policy periodically to reflect changes in our practices or legal requirements. Any updates will be posted on this page with a revised effective date. We encourage you to review this policy regularly to stay informed about how we protect your information.
          </PrivacyBlock>

          <PrivacyBlock title="Contact Us">
            If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal information, please reach out to us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} aria-label="Contact Moonlit">
              {CONTACT_EMAIL}
            </a>. We are committed to addressing your privacy concerns promptly and transparently.
          </PrivacyBlock>
        </motion.div>
      </div>
      
      <Footer />
    </>
  );
};

const PrivacyBlock = ({ title, children }) => (
  <motion.section variants={fadeUp} className="privacy-block">
    <h3>{title}</h3>
    <div className="privacy-text">{children}</div>
  </motion.section>
);

export default Privacy;